import { logger } from "@/lib/logger";
/**
 * Unified Sync Orchestrator
 * Coordinates all offline sync mechanisms to prevent overwhelming
 * weak 2G/EDGE connections when coming back online.
 * 
 * Instead of 5 independent `online` listeners all firing at once,
 * this orchestrator staggers them with configurable delays.
 * 
 * CRASH-SAFE: All operations wrapped in try-catch
 */

import { hasWindow, safeIsOnline } from '@/lib/browserSafeUtils';
import { withTimeout, jitterMs } from '@/lib/net/requestTimeout';

/** A single task may never block the queue longer than this. */
const TASK_TIMEOUT_MS = 120_000;
/** Spread reconnect bursts: thousands of devices regaining signal together. */
const RECONNECT_JITTER_MS = 20_000;

interface SyncTask {
  name: string;
  fn: () => Promise<any>;
  delayMs: number;
  priority: number; // Lower = higher priority
}

const registeredTasks: SyncTask[] = [];
let isOrchestratedSyncRunning = false;
let orchestratorInitialized = false;

/**
 * Register a sync function with the orchestrator.
 * Tasks are executed in priority order with staggered delays.
 */
export function registerSyncTask(
  name: string, 
  fn: () => Promise<any>, 
  options: { delayMs?: number; priority?: number } = {}
): void {
  // Prevent duplicate registrations
  const existing = registeredTasks.findIndex(t => t.name === name);
  if (existing !== -1) {
    registeredTasks[existing] = {
      name,
      fn,
      delayMs: options.delayMs ?? 2000,
      priority: options.priority ?? 5,
    };
    return;
  }

  registeredTasks.push({
    name,
    fn,
    delayMs: options.delayMs ?? 2000,
    priority: options.priority ?? 5,
  });
}

/**
 * Run all registered sync tasks in priority order with staggered delays.
 * Only one orchestrated sync can run at a time.
 */
async function runOrchestratedSync(withJitter = false): Promise<void> {
  if (isOrchestratedSyncRunning || !safeIsOnline()) return;
  isOrchestratedSyncRunning = true;

  logger.debug(`[syncOrchestrator] Connection restored — orchestrating ${registeredTasks.length} sync tasks`);

  // Sort by priority (lower number = higher priority)
  const sorted = [...registeredTasks].sort((a, b) => a.priority - b.priority);

  // Initial 1s for the connection to settle + random spread so a whole
  // district reconnecting at once does not hit the server in the same second.
  let cumulativeDelay = 1000 + (withJitter ? jitterMs(RECONNECT_JITTER_MS) : 0);

  try {

  for (const task of sorted) {
    if (!safeIsOnline()) {
      logger.debug('[syncOrchestrator] Lost connection during sync, aborting remaining tasks');
      break;
    }

    try {
      // Wait the staggered delay
      await new Promise(resolve => setTimeout(resolve, cumulativeDelay));
      
      if (!safeIsOnline()) break;

      logger.debug(`[syncOrchestrator] Running: ${task.name}`);
      await withTimeout(task.fn(), TASK_TIMEOUT_MS, `Sync task ${task.name}`);
      logger.debug(`[syncOrchestrator] Completed: ${task.name}`);
    } catch (error) {
      logger.warn(`[syncOrchestrator] Task "${task.name}" failed:`, error);
      // Continue with other tasks
    }

    cumulativeDelay = task.delayMs; // Use task-specific delay between subsequent tasks
  }
  } finally {
    isOrchestratedSyncRunning = false;
  }
  logger.debug('[syncOrchestrator] Orchestrated sync complete');
}

/**
 * Initialize the orchestrator — replaces individual `online` listeners.
 * Should be called once at app startup.
 */
export function initSyncOrchestrator(): void {
  if (!hasWindow() || orchestratorInitialized) return;
  orchestratorInitialized = true;

  window.addEventListener('online', () => {
    logger.debug('[syncOrchestrator] Online event detected');
    runOrchestratedSync(true);
  });

  // Also provide a manual trigger
  window.addEventListener('sync-orchestrator-run', () => {
    runOrchestratedSync();
  });

  logger.debug('[syncOrchestrator] Initialized');
}

/**
 * Manually trigger an orchestrated sync (e.g., from a "Retry" button)
 */
export async function triggerOrchestratedSync(): Promise<void> {
  await runOrchestratedSync();
}

/**
 * Get registered task names (for debugging)
 */
export function getRegisteredSyncTasks(): string[] {
  return registeredTasks.map(t => `${t.name} (priority: ${t.priority}, delay: ${t.delayMs}ms)`);
}
